import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { JobTitleDocumentModel } from '../models/hiring/jobTitleDocument.model';

import { FileService } from '@core/services/file.service';

import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ApplicantDependentModel } from 'services/models/hiring/applicantDependent.model';
import { ApplicantDocumentModel } from 'services/models/hiring/applicantDocument.model';
import { HiringEventsModel } from 'services/models/hiring/hiring-events.model';
import { Filter } from 'services/models/odata/filter';
import { QueryParams } from 'services/models/odata/queryParams';
import { environment } from '../../../environments/environment';
import { HttpService } from '../http.service';
import { ApplicantModel } from '../models/hiring/applicant.model';
import { ApplicantFormModel } from '../models/hiring/applicantForm.model';
import { Response } from '../models/response';
import { ServiceBase } from '../servicebase';
import { ReportHiringIndiceModel } from 'services/models/hiring/reportHiringIndices.model';
import { HiringStatusType } from 'services/enums/HiringStatusType.enum';
import { FileContentModel } from 'services/models/fileContent';
import {
  HttpBackend,
  HttpClient,
  HttpContext,
  HttpEventType,
  HttpHeaders,
} from '@angular/common/http';
import { BYPASS_LOG } from '@core/interceptors/loading-screen.interceptor';
import { ApplicantFormDocumentModel } from 'services/models/hiring/applicantFormDocuments.model';
import { ObjectMapper } from 'json-object-mapper';
import { ApplicantOcrDocumentModel } from 'services/models/hiring/applicant-ocr-document.model';

type SendAdditionalDocumentPayload = {
  documentTitle: string;
  message: string;
  file: FileContentModel;
};

export type AdditionalDocumentItem = {
  id: string;
  title: string;
  fileName: string;
  sentBy: string;
  creationDate: string;
  changeDate?: string;
};

@Injectable()
export class ApplicantService extends ServiceBase<ApplicantModel> {
  protected httpClientNoInterceptors: HttpClient;

  constructor(
    public http: HttpService,
    public handler: HttpBackend,
    public file: FileService,
    public httpClient: HttpClient
  ) {
    super('applicant', http, environment.apiUrl);
    this.httpClientNoInterceptors = new HttpClient(handler);
  }

  createEntity(input: any): ApplicantModel {
    return ApplicantModel.create(input);
  }

  getFormDocuments(
    cpf: string,
    ignoreStatus = false
  ): Observable<Response<JobTitleDocumentModel[]>> {
    return this.http
      .get(`${this.endpoint}/form-documents/${cpf}/${ignoreStatus}`, null, null, null, true)
      .pipe(
        map(res => {
          const response = new Response<JobTitleDocumentModel[]>();
          response.data = res.body.map(
            pp => JobTitleDocumentModel.create(pp) as JobTitleDocumentModel
          );
          response.count = res.headers.get('x-count');
          return response;
        })
      );
  }

  getApplicantFormById(id: string): Observable<ApplicantFormModel> {
    return new Observable<ApplicantFormModel>(observer => {
      this.http.get(`${this.endpoint}/${id}`).subscribe(
        res => {
          observer.next(ApplicantFormModel.create(res.body));
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  getDependentsByApplicantId(id: string): Observable<Response<ApplicantDependentModel[]>> {
    return this.http.get(`${this.endpoint}/applicant-dependents/${id}`).pipe(
      map(res => {
        const response = new Response<ApplicantDependentModel[]>();
        if (res.body)
          response.data = res.body.map(
            pp => ApplicantDependentModel.create(pp) as ApplicantDependentModel
          );
        response.count = res.headers.get('x-count');
        return response;
      })
    );
  }

  getByCPF(cpf: string, disabledLoading: boolean = false): Observable<ApplicantFormModel> {
    return new Observable<ApplicantFormModel>(observer => {
      this.http
        .get(`${this.endpoint}/bycpf-to-fill/${cpf}`, null, null, null, disabledLoading)
        .subscribe(
          res => {
            observer.next(ApplicantFormModel.create(res.body));
            observer.complete();
          },
          err => observer.error(err)
        );
    });
  }

  getApplicantByCPF(cpf: string): Observable<ApplicantFormModel> {
    return new Observable<ApplicantFormModel>(observer => {
      this.http.get(`${this.endpoint}/by-cpf/${cpf}`).subscribe(
        res => {
          observer.next(ApplicantFormModel.create(res.body));
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  GetNextStage(id: string): Observable<any> {
    return new Observable<ApplicantFormModel>(observer => {
      this.http.get(`${this.endpoint}/${id}/next-stage`).subscribe(
        res => {
          observer.next(res.body);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  fillAdmission(form: ApplicantFormModel = null, docs: any = null): Observable<any> {
    const sanitizedForm = form ? this.sanitizeApplicantFormForFillAdmission(form) : null;
    const obj = {
      applicantForm: sanitizedForm,
      applicantFormDocument: docs,
    };
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/fill-admission`, obj).subscribe(
        res => {
          observer.next(res);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  private sanitizeApplicantFormForFillAdmission(form: ApplicantFormModel): any {
    const plain: any = { ...(form as any) };

    this.sanitizeDateKeys(plain, [
      'birthDate',
      'rgDate',
      'ctpsIssueDate',
      'dateStart',
      'completionDeadline',
      'contractEnd',
    ]);

    plain.practicalTestDates = this.sanitizeDateArray(plain.practicalTestDates);
    plain.theoreticalTestDates = this.sanitizeDateArray(plain.theoreticalTestDates);

    if (Array.isArray(plain.dependents)) {
      plain.dependents = plain.dependents.map((d: any) => ({
        ...(d as any),
        birthDate: this.sanitizeDateValue(d?.birthDate),
      }));
    }

    return plain;
  }

  private sanitizeDateKeys(obj: any, keys: string[]): void {
    keys.forEach(key => (obj[key] = this.sanitizeDateValue(obj[key])));
  }

  private sanitizeDateArray(value: any): any[] | undefined {
    if (!Array.isArray(value)) return value;

    const sanitized = value
      .map(x => this.sanitizeDateValue(x))
      .filter(x => x !== undefined);

    return sanitized.length ? sanitized : undefined;
  }

  private sanitizeDateValue(value: any): any {
    if (value === null || value === undefined) return undefined;
    if (value instanceof Date || typeof value !== 'string') return value;

    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed === 'Invalid date' || trimmed === '01/01/0001' || trimmed.includes('_')) return undefined;

    const matchBr = /^\d{2}\/\d{2}\/\d{4}$/.test(trimmed);
    return matchBr ? trimmed : undefined;
  }

  public addAdmissionDocuments(obj: ApplicantFormDocumentModel): Observable<any> {
    return this.http.post(`${this.endpoint}/add-admission-documents`, obj, null, null, true, true);
  }

  fiilWorksafety(form: any): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/fill-worksafety`, form).subscribe(res => {
        observer.next(res);
        observer.complete();
      });
    });
  }

  listByStatus(status: HiringStatusType): Observable<ApplicantModel[]> {
    return new Observable<ApplicantModel[]>(observer => {
      this.list({
        select: ['name', 'id'],
        orderBy: 'name',
        orderByOrder: 'asc',
        filter: Filter.equal('hiringStatus', status.id),
      } as QueryParams).subscribe(res => {
        observer.next(res.data);
        observer.complete();
      });
    });
  }

  getDocuments(
    id: string,
    disabledLoading: boolean = false
  ): Observable<Response<ApplicantDocumentModel[]>> {
    return this.http
      .get(`${this.endpoint}/documents/${id}`, null, null, null, disabledLoading)
      .pipe(
        map(res => {
          const response = new Response<ApplicantDocumentModel[]>();
          response.data = res.body.map(
            pp => ApplicantDocumentModel.create(pp) as ApplicantDocumentModel
          );
          response.count = res.headers.get('x-count');
          return response;
        })
      );
  }

  getInvalidDocuments(id: string): Observable<Response<ApplicantDocumentModel[]>> {
    return this.http.get(`${this.endpoint}/invalid-documents/${id}`).pipe(
      map(res => {
        const response = new Response<ApplicantDocumentModel[]>();
        response.data = res.body.map(
          pp => ApplicantDocumentModel.create(pp) as ApplicantDocumentModel
        );
        response.count = res.headers.get('x-count');
        return response;
      })
    );
  }

  changeApplicantStatus(id: string, status: string): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/applicant-status/${id}/${status}`, '').subscribe(res => {
        observer.next(res);
        observer.complete();
      });
    });
  }

  resendApplicantLink(id: string, dateStart: string): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/resendLink/${id}/${dateStart}`, '').subscribe(res => {
        observer.next(res);
        observer.complete();
      });
    });
  }

  changeApplicantDateStart(id: string, dateStart: string): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/change-date-start/${id}/${dateStart}`, '').subscribe(res => {
        observer.next(res);
        observer.complete();
      });
    });
  }

  getHiringEvents(id: string): Observable<HiringEventsModel[]> {
    return new Observable<HiringEventsModel[]>(observer => {
      this.http.get(`${this.endpoint}/hiring-events/${id}`).subscribe(
        res => {
          observer.next(res.body.map(x => HiringEventsModel.create(x)));
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  getReportHiringIndices(
    begin: string,
    end: string,
    companyIds: string[]
  ): Observable<ReportHiringIndiceModel> {
    const searchParams = new URLSearchParams();
    companyIds.forEach((companyIds: string) => searchParams.append('companyIds', companyIds));

    const params = {
      select: [],
      filter: `&${searchParams.toString()}`,
    } as QueryParams;
    const query = this.createBuildQuery(params);

    return new Observable<ReportHiringIndiceModel>(observer => {
      this.http.get(`${this.endpoint}/report-hiring-indices/${begin}/${end}${query}`).subscribe(
        res => {
          observer.next(ReportHiringIndiceModel.create(res.body));
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  exportCsv(params: QueryParams, disableLoading: boolean = false): Observable<any> {
    return this.http.get(
      `${this.endpoint}/export-by-filters/odata${params ? this.createBuildQuery(params) : ''}`,
      { responseType: 'arraybuffer' },
      this.baseUrl,
      null,
      disableLoading
    );
  }

  exportEligibilityVerificationFile(dateBegin: string, dateEnd: string) {
    return this.http
      .get(
        `${this.endpoint}/export-eligibility-verification-file?dateBegin=${dateBegin}&dateEnd=${dateEnd}`
      )
      .pipe(
        map((res: any) => {
          const blob = this.file.base64ToBlob(res.body.fileContents);
          const type = 'txt';
          this.file.saveFile(blob, `${res.body.fileDownloadName}.${type}`);
          return blob;
        })
      );
  }

  importEligibilityVerificationFile(payload: any): Observable<any> {
    return new Observable<any>(observer => {
      this.http
        .post(`${this.endpoint}/import-eligibility-verification-file`, payload)
        .subscribe(res => {
          observer.next(res);
          observer.complete();
        });
    });
  }

  updateEligibilityVerificationStatus(payload: any): Observable<any> {
    return new Observable<any>(observer => {
      this.http
        .put(`${this.endpoint}/update-eligibility-verification-status`, payload)
        .subscribe(res => {
          observer.next(res);
          observer.complete();
        });
    });
  }

  sendASO(payload: any): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/send-aso`, payload).subscribe(
        res => {
          observer.next(res);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  private downloadFile(id: string, path: string, defaultFilename: string): void {
    this.http
      .get(`${this.endpoint}/${id}/${path}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe(
        response => {
          const blob = response.body!;
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = defaultFilename;
          if (contentDisposition) {
            const utfFilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
            if (utfFilenameMatch && utfFilenameMatch[1]) {
              filename = decodeURIComponent(utfFilenameMatch[1]);
            }
          }
          this.file.saveFile(blob, filename);
          this.file.openFile(blob);
        },
        error => {
          console.error(`Erro ao baixar o arquivo ${defaultFilename}:`, error);
        }
      );
  }

  private openFile(id: string, path: string): void {
    this.http
      .get(`${this.endpoint}/${id}/${path}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe(
        response => {
          const blob = response.body!;
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
        },
        error => {
          console.error(`Erro ao abrir o arquivo (${path}):`, error);
        }
      );
  }

  downloadAdmissionKitFile(id: string) {
    this.downloadFile(id, 'admission-kit-file', 'kit-admissional');
  }

  openAdmissionKitFile(id: string) {
    this.openFile(id, 'admission-kit-file');
  }

  uploadAdmissionKitFile(id: string, obj: FileContentModel): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/${id}/upload-admission-kit-file`, obj).subscribe(
        res => {
          observer.next(res);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  downloadBenefitsKitFile(id: string) {
    this.downloadFile(id, 'benefits-kit-file', 'kit-beneficios');
  }

  openBenefitsKitFile(id: string) {
    this.openFile(id, 'benefits-kit-file');
  }

  uploadBenefitsKitFile(id: string, obj: FileContentModel): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/${id}/upload-benefits-kit-file`, obj).subscribe(
        res => {
          observer.next(res);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  sendAdditionalDocument(id: string, payload: SendAdditionalDocumentPayload): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/${id}/send-additional-document`, payload).subscribe(
        res => {
          observer.next(res);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  resendAdditionalDocument(id: string, documentId: string): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/${id}/resend-additional-document/${documentId}`, '').subscribe(
        res => {
          observer.next(res);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  getAdditionalDocuments(id: string): Observable<Response<AdditionalDocumentItem[]>> {
    return this.http
      .get(`${this.endpoint}/${id}/additional-documents`)
      .pipe(
        map(res => {
          const response = new Response<AdditionalDocumentItem[]>();
          response.data = (res.body || []) as AdditionalDocumentItem[];
          response.count = res.headers.get('x-count');
          return response;
        })
      );
  }

  uploadAdmissionExamResults(id: string, obj: FileContentModel): Observable<any> {
    return new Observable<any>(observer => {
      this.http.post(`${this.endpoint}/${id}/upload-admission-exam-results`, obj).subscribe(
        res => {
          observer.next(res);
          observer.complete();
        },
        err => observer.error(err)
      );
    });
  }

  openAdmissionExamResults(id: string) {
    this.openFile(id, 'admission-exam-results');
  }

  downloadAdmissionExamResults(id: string) {
    this.downloadFile(id, 'admission-exam-results', 'resultado-exame');
  }

  downloadAdmissionExamPetitionFile(id: string) {
    this.downloadFile(id, 'admission-exam-petition-file', 'peticao-exame');
  }

  changePreliminaryCourseStatus(payload: any): Observable<Response<any>> {
    return this.http.put(`${this.endpoint}/preliminary-course/change-status`, payload).pipe(
      map(res => {
        const response = new Response<any>();
        response.data = res;
        return response;
      })
    );
  }

  notifyPreliminaryCourse(payload: any): Observable<Response<any>> {
    return this.http.put(`${this.endpoint}/preliminary-course/notify`, payload).pipe(
      map(res => {
        const response = new Response<any>();
        response.data = res;
        return response;
      })
    );
  }

  updateGeneratedOfferLetter(applicant: ApplicantModel): Observable<Response<any>> {
    return this.http
      .put(`${this.endpoint}/update-generated-offer-letter`, {
        id: applicant.id,
        generatedOfferLetter: applicant.generatedOfferLetter,
      })
      .pipe(
        map(res => {
          const response = new Response<any>();
          response.data = res;
          return response;
        })
      );
  }

  deleteDependent(id: string): Observable<any> {
    if (!id) {
      throw new Error('Nenhum ID encontrado');
    }

    const opt = {
      context: new HttpContext().set(BYPASS_LOG, true),
    };

    const url = `${environment.apiUrl}/${this.endpoint}/applicant-dependents/remove/${id}`;
    return this.httpClient.delete<any>(url, opt).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  registerManyApplicantDependents(dependents: ApplicantDependentModel[]): Observable<any> {
    return new Observable<any>(observer => {
      this.http
        .post(`${this.endpoint}/applicant-dependents/register-many`, dependents, null, null, true)
        .subscribe(res => {
          observer.next(res);
          observer.complete();
        });
    });
  }

  public uploadFileWithPreSignedUrl(
    applicant: ApplicantModel,
    file: File
  ): Observable<{ url: string; fileKey: string }> {
    return this.getPreSignedUrl(applicant, file.name, file.type).pipe(
      switchMap(preSigned =>
        this.httpClientNoInterceptors
          .put(preSigned.url, file, {
            headers: new HttpHeaders({
              'Content-Type': file.type,
            }),
            observe: 'events',
            reportProgress: false,
          })
          .pipe(
            // ✅ garante UMA ÚNICA emissão
            filter(event => event.type === HttpEventType.Response),
            map(() => preSigned)
          )
      )
    );
  }

  public getPreSignedUrl(
    applicant: ApplicantModel,
    fileName: string,
    contentType: string
  ): Observable<{ url: string; fileKey: string }> {
    const params = {
      applicantId: applicant.id,
      companyId: applicant.companyId,
      companyGroupId: applicant.companyGroupId,
      fileName,
      contentType,
    };

    return this.httpClientNoInterceptors.get<{ url: string; fileKey: string }>(
      `${environment.apiUrl}/${this.endpoint}/add-admission-documents/pre-signed-url-put`,
      { params }
    );
  }

  getApplicantOcrData(applicantId: string): Observable<ApplicantOcrDocumentModel[]> {
    return this.http.get(`${this.endpoint}/${applicantId}/list-ocr-data`, null, null, null, true);
  }
}
